import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelType } from '@/enums/model-type.enum';
import { useModelBaseView } from '@/components/Recipe/FlowContext';
import { I18N_KEYS } from '@/language/keys';
import { HandleType } from '@/enums/handle-type.enum';
import { AddItemButton } from '../../../Common/AddItemButton/AddItemButton';
import { MODEL_INPUT_LIMITS } from '../consts/model-input-limits.const';
import type { ModelBaseNodeData } from '@/types/nodes/model';
import type { Handle } from '@/types/node';

interface AddInputButtonProps {
  canEdit: boolean;
  modelName: string;
  nodeId: string;
  data: ModelBaseNodeData;
}

const FAL_FLUX_MULTI_LORA = ModelType.FalFluxMultiLora.valueOf();
const FAL_FLUX_MULTI_LORA_INPAINT = ModelType.FalFluxMultiLoraInpaint.valueOf();
const FAL_FLUX_KONTEXT_LORA = ModelType.FalFluxKontextLora.valueOf();

type ModelInputLimits = {
  reachedLimit: boolean;
  maxInputs: number | null;
};

const checkModelInputLimits = (
  modelName: string,
  inputHandles: string[] | Record<string, Handle>,
  inputPrefix: string | null,
): ModelInputLimits => {
  const modelLimits = MODEL_INPUT_LIMITS[modelName];

  if (!modelLimits || !inputPrefix || Array.isArray(inputHandles)) {
    return { reachedLimit: false, maxInputs: null };
  }

  const currentCount = Object.keys(inputHandles).filter((key) => key.includes(inputPrefix)).length;
  const reachedLimit = currentCount >= modelLimits.maxInputs;

  return {
    reachedLimit,
    maxInputs: modelLimits.maxInputs,
  };
};

const getInputLimitTypeKey = (inputPrefix: string | undefined): string => {
  const footerKeys = I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER;

  switch (inputPrefix) {
    case 'element':
      return footerKeys.ADD_INPUT_LIMIT_TYPE_ELEMENT;
    case 'reference_image':
      return footerKeys.ADD_INPUT_LIMIT_TYPE_REFERENCE_IMAGE;
    default:
      return footerKeys.ADD_INPUT_LIMIT_TYPE_INPUT;
  }
};

export const AddInputButton = ({ canEdit, modelName, nodeId, data }: AddInputButtonProps) => {
  const { t } = useTranslation();
  const modelBaseView = useModelBaseView(nodeId);

  const handleAddLoraInputHandle = useCallback(() => {
    modelBaseView.addLoraInputHandles();
  }, [modelBaseView]);

  const handleAddInputHandle = useCallback(
    (prefix: string, type: HandleType) => {
      modelBaseView.addInputHandle(prefix, type);
    },
    [modelBaseView],
  );

  const dynamicInputConfig = useMemo(() => {
    return modelBaseView.getDynamicInputConfig(modelName);
  }, [modelBaseView, modelName]);

  const isFalFluxMultiLora =
    modelName === FAL_FLUX_MULTI_LORA ||
    modelName === FAL_FLUX_MULTI_LORA_INPAINT ||
    modelName === FAL_FLUX_KONTEXT_LORA;

  const shouldShowAddInputButton = useMemo(() => {
    return canEdit && (isFalFluxMultiLora || !!dynamicInputConfig);
  }, [canEdit, isFalFluxMultiLora, dynamicInputConfig]);

  const { reachedLimit, maxInputs } = useMemo(() => {
    return checkModelInputLimits(modelName, data.handles.input, dynamicInputConfig?.prefix ?? null);
  }, [modelName, data.handles.input, dynamicInputConfig]);

  const buttonText = useMemo(() => {
    if (isFalFluxMultiLora) {
      return t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ANOTHER_LORA);
    }
    if (dynamicInputConfig?.handleType === HandleType.KlingElement) {
      return t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ELEMENT);
    }
    if (dynamicInputConfig?.handleType === HandleType.Video) {
      return t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ANOTHER_VIDEO_INPUT);
    }

    if (dynamicInputConfig?.handleType === HandleType.Image) {
      return t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ANOTHER_IMAGE_INPUT);
    }

    return t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ANOTHER_INPUT);
  }, [isFalFluxMultiLora, dynamicInputConfig, t]);

  const handleClick = useMemo(() => {
    if (isFalFluxMultiLora) return handleAddLoraInputHandle;
    if (dynamicInputConfig) return () => handleAddInputHandle(dynamicInputConfig.prefix, dynamicInputConfig.handleType);
    return () => {};
  }, [isFalFluxMultiLora, dynamicInputConfig, handleAddLoraInputHandle, handleAddInputHandle]);

  return (
    <AddItemButton
      show={shouldShowAddInputButton}
      text={buttonText}
      onClick={handleClick}
      disabled={reachedLimit}
      disabledTooltipText={
        reachedLimit
          ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.ADD_ANOTHER_IMAGE_INPUT_DISABLED, {
              maxInputs,
              inputType: t(getInputLimitTypeKey(dynamicInputConfig?.prefix), { count: maxInputs ?? 0 }),
            })
          : undefined
      }
    />
  );
};
